// Types for merchant application field settings and source tracking

import type { PipelineStatus } from './index';
import type { IndustryClassificationSettings } from './industryClassifications';

export type ApplicationSource = 'InvoiceBug' | 'Direct' | 'Partner' | 'Referral' | 'Other' | string;

// Default sources that cannot be deleted
export const DEFAULT_APPLICATION_SOURCES = ['InvoiceBug', 'Direct', 'Partner', 'Referral', 'Other'] as const;

// Section names that map to application steps
export const APPLICATION_SECTIONS = {
  PERSONAL_INFO: 'Personal Information',
  CORPORATION_INFO: 'Corporation Information',
  PROCESSING_HISTORY: 'Processing History',
  BENEFICIAL_OWNERS: 'Beneficial Owners',
  BANK_DETAILS: 'Bank Details',
  DOCUMENTATION: 'Documentation',
} as const;

export type ApplicationSection = typeof APPLICATION_SECTIONS[keyof typeof APPLICATION_SECTIONS];

export interface FieldSetting {
  fieldName: string;
  fieldPath: string; // Path in the form data (e.g., 'personalInfo.firstName')
  label: string;
  section: string; // Which section this field belongs to
  enabled: boolean;
  required: boolean;
  order?: number; // Custom order within the section
  defaultValue?: any;
  placeholder?: string;
  helpText?: string;
  // Optional: control whether UI renders this field as a clickable link (e.g., website/email/phone)
  linkEnabled?: boolean;
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    min?: number;
    max?: number;
  };
}

export interface DocumentRequirement {
  documentType: string;
  label: string;
  enabled: boolean;
  required: boolean;
  processingVolumeThreshold?: number; // Monthly volume in dollars
  description?: string;
  acceptedFormats?: string[];
  maxFileSize?: number; // In MB
}

export interface SectionConfig {
  title: string;
  subtitle: string;
  displayType: 'page' | 'section'; // Whether this section creates a new page or is part of the previous page
  order: number; // Display order
}

// Settings for a specific industry classification (Low Risk or High Risk)
export interface ClassificationSettings {
  fieldSettings: Record<string, FieldSetting>;
  documentRequirements: DocumentRequirement[];
  sectionVisibility?: Record<string, boolean>; // Section name -> visibility
  sectionOrder?: string[]; // Custom order for sections
  sectionConfigs?: Record<string, SectionConfig>; // Section name -> configuration
}

// Configuration for a specific banking partner (contains both low-risk and high-risk settings)
export type BankingPartnerConfiguration = Record<IndustryClassification, ClassificationSettings>;

export interface ApplicationSourceSettings {
  source: ApplicationSource;
  displayName: string;
  defaultPipelineStatus?: PipelineStatus; // Default pipeline status for new applications from this source
  isCustom?: boolean; // Whether this is a custom source (can be deleted)

  // Per-source Banking Partner configuration
  bankingPartnerId?: string; // Banking partner ID for this source
  bankingPartnerApiKey?: string; // PayArc API key for this source

  customQuestions?: {
    question: string;
    fieldName: string;
    fieldType: 'text' | 'number' | 'select' | 'checkbox';
    options?: string[]; // For select fields
    required: boolean;
  }[];

  // New nested structure: Banking Partner -> Industry Classification -> Settings
  bankingPartnerConfigurations?: Record<string, BankingPartnerConfiguration>;

  // Legacy fields (for backwards compatibility during migration)
  fieldSettings?: Record<string, FieldSetting>;
  documentRequirements?: DocumentRequirement[];
  industryClassifications?: IndustryClassificationSettings; // Industry codes configuration
  sectionVisibility?: Record<string, boolean>;
  sectionOrder?: string[];
  sectionConfigs?: Record<string, SectionConfig>;
}

export type IndustryClassification = 'low-risk' | 'high-risk';

export interface MerchantApplicationSettings {
  sources: Record<string, ApplicationSourceSettings>; // Changed to string to support dynamic sources
  defaultSource: ApplicationSource;
  trackingEnabled: boolean;
  defaultBankingPartnerId?: string;
  defaultBankingPartnerApiKey?: string;
  industryClassification?: IndustryClassification;
}

// Default field definitions for merchant applications
export const DEFAULT_FIELD_DEFINITIONS: Record<string, Partial<FieldSetting>> = {
  // Personal Information
  'personalInfo.firstName': {
    label: 'First Name',
    section: 'Personal Information',
    placeholder: 'John',
    order: 1,
  },
  'personalInfo.lastName': {
    label: 'Last Name',
    section: 'Personal Information',
    placeholder: 'Doe',
    order: 2,
  },
  'personalInfo.email': {
    label: 'Email Address',
    section: 'Personal Information',
    placeholder: 'john@example.com',
    order: 3,
  },
  'personalInfo.phone': {
    label: 'Phone Number',
    section: 'Personal Information',
    placeholder: '(XXX) XXX-XXXX',
    order: 4,
  },

  // Individual Information - REMOVED: Fields moved to Beneficial Owners section

  // Corporation Information (Consolidated from Business Information)
  'corporationInfo.businessName': {
    label: 'Business Legal Name',
    section: 'Corporation Information',
    placeholder: 'ABC Company LLC',
    order: 1,
  },
  'corporationInfo.dba': {
    label: 'DBA (Doing Business As)',
    section: 'Corporation Information',
    placeholder: 'Optional',
    order: 2,
  },
  'corporationInfo.corporationType': {
    label: 'Corporation Type',
    section: 'Corporation Information',
    placeholder: 'LLC, Corp, Partnership, etc.',
    order: 3,
  },
  'corporationInfo.businessStartDate': {
    label: 'Business Start Date',
    section: 'Corporation Information',
    placeholder: 'MM/DD/YYYY',
    order: 4,
  },
  'corporationInfo.ein': {
    label: 'EIN (Employer Identification Number)',
    section: 'Corporation Information',
    placeholder: 'XX-XXXXXXX',
    order: 5,
  },
  'corporationInfo.website': {
    label: 'Website',
    section: 'Corporation Information',
    placeholder: 'https://example.com',
    linkEnabled: true,
    order: 6,
  },
  'corporationInfo.businessDescription': {
    label: 'Business Description',
    section: 'Corporation Information',
    placeholder: 'Describe what your business does...',
    order: 7,
  },
  'corporationInfo.sicCode': {
    label: 'SIC/MCC Code',
    section: 'Corporation Information',
    placeholder: '7012',
    helpText: 'Standard Industrial Classification or Merchant Category Code',
    order: 8,
  },
  'corporationInfo.sicDescription': {
    label: 'SIC Code Description',
    section: 'Corporation Information',
    placeholder: 'Auto-populated description',
    helpText: 'Auto-populated description of the SIC code',
    order: 9,
  },
  'corporationInfo.customerServiceEmail': {
    label: 'Customer Service Email',
    section: 'Corporation Information',
    placeholder: 'support@example.com',
    linkEnabled: true,
    order: 10,
  },
  'corporationInfo.customerServicePhone': {
    label: 'Customer Service Phone',
    section: 'Corporation Information',
    placeholder: '(XXX) XXX-XXXX',
    linkEnabled: true,
    order: 11,
  },
  'corporationInfo.businessAddress': {
    label: 'Business Address',
    section: 'Corporation Information',
    placeholder: 'Complete address',
    helpText: 'Your business address',
    order: 12,
  },
  'corporationInfo.businessAddress.street': {
    label: 'Street Address',
    section: 'Corporation Information',
    placeholder: '123 Main Street',
    order: 13,
  },
  'corporationInfo.businessAddress.city': {
    label: 'City',
    section: 'Corporation Information',
    placeholder: 'City',
    order: 14,
  },
  'corporationInfo.businessAddress.state': {
    label: 'State',
    section: 'Corporation Information',
    placeholder: 'CA',
    order: 15,
  },
  'corporationInfo.businessAddress.zipCode': {
    label: 'ZIP Code',
    section: 'Corporation Information',
    placeholder: '12345',
    order: 16,
  },


  // Processing History
  'processingHistory.isCurrentlyProcessing': {
    label: 'Currently Processing Payments?',
    section: 'Processing History',
    placeholder: 'Yes/No',
    order: 1,
  },
  'processingHistory.monthlyVolume': {
    label: 'Monthly Processing Volume',
    section: 'Processing History',
    placeholder: '10000',
    helpText: 'Average monthly processing volume in dollars',
    order: 2,
  },
  'processingHistory.averageTicket': {
    label: 'Average Transaction Amount',
    section: 'Processing History',
    placeholder: '100',
    order: 3,
  },
  'processingHistory.highTicket': {
    label: 'Highest Transaction Amount',
    section: 'Processing History',
    placeholder: '1000',
    order: 4,
  },
  'processingHistory.salesDeliveryDays': {
    label: 'Average Sales Delivery Days',
    section: 'Processing History',
    placeholder: '0',
    helpText: 'Typical number of days between sale and delivery',
    defaultValue: 0,
    order: 5,
  },
  'processingHistory.whenIsCustomerCharged': {
    label: 'When do you charge the credit card?',
    section: 'Processing History',
    helpText: 'Select when the customer is charged during the order lifecycle.',
    defaultValue: 'order',
    order: 6,
  },
  'processingHistory.orderTurnaroundTime': {
    label: 'Average Order Turnaround (days)',
    section: 'Processing History',
    placeholder: '7',
    helpText: 'Typical number of days to fulfill or deliver an order.',
    defaultValue: 7,
    validation: {
      min: 0,
    },
    order: 7,
  },
  'processingHistory.advancedDeposits': {
    label: 'Collect Advanced Deposits?',
    section: 'Processing History',
    helpText: 'Do you collect deposits before fulfillment?',
    defaultValue: 'no',
    order: 8,
  },
  'processingHistory.depositPct': {
    label: 'Deposit Percentage (%)',
    section: 'Processing History',
    placeholder: '0',
    helpText: 'Percentage of the order amount collected upfront.',
    defaultValue: 0,
    validation: {
      min: 0,
      max: 100,
    },
    order: 9,
  },
  'processingHistory.currentProcessor': {
    label: 'Current Payment Processor',
    section: 'Processing History',
    placeholder: 'Stripe, Square, etc.',
    order: 10,
  },
  'processingHistory.acceptOfflineCards': {
    label: 'Accept Offline/Terminal Card Payments?',
    section: 'Processing History',
    helpText: 'Do you accept credit cards through physical terminals?',
    defaultValue: false,
    order: 11,
  },
  'processingHistory.retailPercentage': {
    label: 'Retail/Terminal Transaction Percentage',
    section: 'Processing History',
    placeholder: '90',
    helpText: 'Percentage of transactions processed through physical terminals (shown only if offline cards accepted)',
    defaultValue: 90,
    order: 12,
  },
  'processingHistory.onlinePercentage': {
    label: 'Online Transaction Percentage',
    section: 'Processing History',
    placeholder: '10',
    helpText: 'Percentage of transactions processed online (auto-calculated from retail percentage)',
    defaultValue: 10,
    order: 13,
  },
  'processingHistory.b2bPercentage': {
    label: 'Business-to-Business Sales Percentage',
    section: 'Processing History',
    placeholder: '50',
    helpText: 'Percentage of sales to other businesses (admin override)',
    defaultValue: 50,
    order: 14,
  },
  'processingHistory.b2cPercentage': {
    label: 'Business-to-Consumer Sales Percentage',
    section: 'Processing History',
    placeholder: '50',
    helpText: 'Percentage of sales to consumers (admin override)',
    defaultValue: 50,
    order: 15,
  },
  'processingHistory.hasBeenTerminated': {
    label: 'Previously Terminated by a Processor?',
    section: 'Processing History',
    placeholder: 'Yes/No',
    order: 16,
  },
  'processingHistory.terminationExplanation': {
    label: 'Termination Explanation',
    section: 'Processing History',
    placeholder: 'Please explain the circumstances',
    order: 17,
  },
  'processingHistory.bankruptcy': {
    label: 'Any History of Bankruptcy?',
    section: 'Processing History',
    helpText: 'Have you or your business filed for bankruptcy in the past 7 years?',
    defaultValue: false,
    order: 18,
  },

  // Beneficial Owners
  'beneficialOwners.firstName': {
    label: 'First Name',
    section: 'Beneficial Owners',
    placeholder: 'John',
    order: 1,
  },
  'beneficialOwners.lastName': {
    label: 'Last Name',
    section: 'Beneficial Owners',
    placeholder: 'Doe',
    order: 2,
  },
  'beneficialOwners.title': {
    label: 'Title/Role',
    section: 'Beneficial Owners',
    helpText: 'Role or title within the company (CEO, CFO, etc.)',
    order: 3,
  },
  'beneficialOwners.ownershipPercentage': {
    label: 'Ownership Percentage',
    section: 'Beneficial Owners',
    placeholder: '25',
    helpText: 'Percentage of business ownership',
    order: 4,
  },
  'beneficialOwners.email': {
    label: 'Email Address',
    section: 'Beneficial Owners',
    placeholder: 'john@example.com',
    order: 5,
  },
  'beneficialOwners.phone': {
    label: 'Phone Number',
    section: 'Beneficial Owners',
    placeholder: '(XXX) XXX-XXXX',
    order: 6,
  },
  'beneficialOwners.ssn': {
    label: 'Social Security Number',
    section: 'Beneficial Owners',
    placeholder: 'XXX-XX-XXXX',
    order: 7,
  },
  'beneficialOwners.dateOfBirth': {
    label: 'Date of Birth',
    section: 'Beneficial Owners',
    placeholder: 'MM/DD/YYYY',
    order: 8,
  },
  'beneficialOwners.driverLicense': {
    label: "Driver's License Number",
    section: 'Beneficial Owners',
    placeholder: 'DL123456789',
    order: 9,
  },
  'beneficialOwners.address': {
    label: 'Address',
    section: 'Beneficial Owners',
    placeholder: '123 Main Street',
    order: 10,
  },
  'beneficialOwners.city': {
    label: 'City',
    section: 'Beneficial Owners',
    placeholder: 'City',
    order: 11,
  },
  'beneficialOwners.state': {
    label: 'State',
    section: 'Beneficial Owners',
    order: 12,
  },
  'beneficialOwners.zipCode': {
    label: 'ZIP Code',
    section: 'Beneficial Owners',
    placeholder: '12345',
    order: 13,
  },

  // Bank Details
  'bankDetails.bankName': {
    label: 'Bank Name',
    section: 'Bank Details',
    placeholder: 'Bank of America',
    order: 1,
  },
  'bankDetails.routingNumber': {
    label: 'Routing Number',
    section: 'Bank Details',
    placeholder: '123456789',
    order: 2,
  },
  'bankDetails.accountNumber': {
    label: 'Account Number',
    section: 'Bank Details',
    placeholder: 'XXXXXXXXXXXX',
    order: 3,
  },
  'bankDetails.accountType': {
    label: 'Bank Account Type',
    section: 'Bank Details',
    helpText: 'Select whether the settlement account is checking or savings.',
    order: 4,
    defaultValue: 'checking',
  },
};

// Default document requirements for credit card processing
export const DEFAULT_DOCUMENT_REQUIREMENTS: DocumentRequirement[] = [
  {
    documentType: 'drivers_license',
    label: "Driver's License",
    enabled: true,
    required: true,
    description: 'Valid government-issued driver\'s license',
    acceptedFormats: ['jpg', 'jpeg', 'png', 'pdf'],
    maxFileSize: 5,
  },
  {
    documentType: 'voided_check',
    label: 'Voided Check',
    enabled: true,
    required: true,
    description: 'Voided check from your business bank account',
    acceptedFormats: ['jpg', 'jpeg', 'png', 'pdf'],
    maxFileSize: 5,
  },
  {
    documentType: 'bank_statements',
    label: 'Bank Statements',
    enabled: true,
    required: false,
    processingVolumeThreshold: 50000, // Required if monthly volume > $50k
    description: 'Last 3 months of business bank statements',
    acceptedFormats: ['pdf'],
    maxFileSize: 10,
  },
  {
    documentType: 'business_license',
    label: 'Business License',
    enabled: true,
    required: false,
    description: 'Business license or registration',
    acceptedFormats: ['jpg', 'jpeg', 'png', 'pdf'],
    maxFileSize: 5,
  },
  {
    documentType: 'tax_returns',
    label: 'Tax Returns',
    enabled: false,
    required: false,
    processingVolumeThreshold: 100000, // Required if monthly volume > $100k
    description: 'Last 2 years of business tax returns',
    acceptedFormats: ['pdf'],
    maxFileSize: 20,
  },
  {
    documentType: 'processing_statements',
    label: 'Processing Statements',
    enabled: true,
    required: false,
    description: 'Last 3 months of credit card processing statements (if currently processing)',
    acceptedFormats: ['pdf'],
    maxFileSize: 10,
  },
];

// Default section order
export const DEFAULT_SECTION_ORDER = [
  APPLICATION_SECTIONS.PERSONAL_INFO,
  APPLICATION_SECTIONS.CORPORATION_INFO,
  APPLICATION_SECTIONS.PROCESSING_HISTORY,
  APPLICATION_SECTIONS.BENEFICIAL_OWNERS,
  APPLICATION_SECTIONS.BANK_DETAILS,
  APPLICATION_SECTIONS.DOCUMENTATION,
];

// Default section configurations
export const DEFAULT_SECTION_CONFIGS: Record<string, SectionConfig> = {
  [APPLICATION_SECTIONS.PERSONAL_INFO]: {
    title: "Tell us about yourself",
    subtitle: "Let's start with some basic information about you. This will help us personalize your application experience.",
    displayType: 'page',
    order: 1,
  },
  [APPLICATION_SECTIONS.CORPORATION_INFO]: {
    title: "Business Information",
    subtitle: "Tell us about your business and how it operates.",
    displayType: 'page',
    order: 2,
  },
  [APPLICATION_SECTIONS.PROCESSING_HISTORY]: {
    title: "Processing History",
    subtitle: "Help us understand your payment processing needs and history.",
    displayType: 'page',
    order: 3,
  },
  [APPLICATION_SECTIONS.BENEFICIAL_OWNERS]: {
    title: "Beneficial Owners",
    subtitle: "List all individuals who own 25% or more of the business.",
    displayType: 'page',
    order: 4,
  },
  [APPLICATION_SECTIONS.BANK_DETAILS]: {
    title: "Bank Details",
    subtitle: "Provide your business banking information for fund deposits.",
    displayType: 'page',
    order: 5,
  },
  [APPLICATION_SECTIONS.DOCUMENTATION]: {
    title: "Documentation",
    subtitle: "Upload required documents to complete your application.",
    displayType: 'page',
    order: 6,
  },
};

// Helper function to create classification settings
export const createClassificationSettings = (): ClassificationSettings => {
  const fieldSettings = Object.keys(DEFAULT_FIELD_DEFINITIONS).reduce((acc, fieldPath) => {
    const definition = DEFAULT_FIELD_DEFINITIONS[fieldPath];
    acc[fieldPath] = {
      fieldName: fieldPath.split('.').pop() || '',
      fieldPath,
      ...definition,
      enabled: true,
      required: true,
    } as FieldSetting;
    return acc;
  }, {} as Record<string, FieldSetting>);

  return {
    fieldSettings,
    documentRequirements: [...DEFAULT_DOCUMENT_REQUIREMENTS],
    sectionVisibility: {
      [APPLICATION_SECTIONS.PERSONAL_INFO]: true,
      [APPLICATION_SECTIONS.CORPORATION_INFO]: true,
      [APPLICATION_SECTIONS.PROCESSING_HISTORY]: true,
      [APPLICATION_SECTIONS.BENEFICIAL_OWNERS]: true,
      [APPLICATION_SECTIONS.BANK_DETAILS]: true,
      [APPLICATION_SECTIONS.DOCUMENTATION]: true,
    },
    sectionOrder: [...DEFAULT_SECTION_ORDER],
    sectionConfigs: { ...DEFAULT_SECTION_CONFIGS },
  };
};

// Default settings template for any application source (generic fallback)
export const DEFAULT_SOURCE_SETTINGS: ApplicationSourceSettings = {
  source: '',  // Will be set dynamically
  displayName: '',  // Will be set dynamically
  defaultPipelineStatus: 'lead',
  isCustom: false,
  // Legacy fields
  sectionConfigs: { ...DEFAULT_SECTION_CONFIGS },
  fieldSettings: Object.keys(DEFAULT_FIELD_DEFINITIONS).reduce((acc, fieldPath) => {
    const definition = DEFAULT_FIELD_DEFINITIONS[fieldPath];
    acc[fieldPath] = {
      fieldName: fieldPath.split('.').pop() || '',
      fieldPath,
      ...definition,
      enabled: true,
      required: true,
    } as FieldSetting;
    return acc;
  }, {} as Record<string, FieldSetting>),
  documentRequirements: DEFAULT_DOCUMENT_REQUIREMENTS,
  sectionVisibility: {
    [APPLICATION_SECTIONS.PERSONAL_INFO]: true,
    [APPLICATION_SECTIONS.CORPORATION_INFO]: true,
    [APPLICATION_SECTIONS.PROCESSING_HISTORY]: true,
    [APPLICATION_SECTIONS.BENEFICIAL_OWNERS]: true,
    [APPLICATION_SECTIONS.BANK_DETAILS]: true,
    [APPLICATION_SECTIONS.DOCUMENTATION]: true,
  },
  sectionOrder: DEFAULT_SECTION_ORDER,
};

// Legacy alias for backwards compatibility
export const DEFAULT_INVOICEBUG_SETTINGS = DEFAULT_SOURCE_SETTINGS;
